import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VerticalStackedBarChart } from '@fluentui/react-charting';
import type { IVSChartDataPoint, IVerticalStackedChartProps } from '@fluentui/react-charting';
import { WidgetContainer } from '../WidgetContainer';
import { ChartThemeProvider } from '../ChartThemeProvider';
import { useBusinessPurposeChartStyles } from './BusinessPurposeChart.styles';
import type { BusinessPurposeWeekData, BusinessPurposeConfig } from '@/types/reports';

export interface BusinessPurposeChartProps {
  data: BusinessPurposeWeekData[];
  purposes: BusinessPurposeConfig[];
}

export const BusinessPurposeChart = ({ data, purposes }: BusinessPurposeChartProps) => {
  const styles = useBusinessPurposeChartStyles();
  const { t } = useTranslation('reports');

  const chartData: IVerticalStackedChartProps[] = useMemo(
    () =>
      data.map((week) => ({
        chartData: purposes.map((purpose): IVSChartDataPoint => ({
          legend: purpose.name,
          data: (week[purpose.name] as number) || 0,
          color: purpose.color,
        })),
        xAxisPoint: week.period,
      })),
    [data, purposes]
  );

  return (
    <WidgetContainer title={t('widgets.businessPurposeTitle')}>
      <div className={styles.root} role="figure" aria-label={t('widgets.businessPurposeTitle')}>
        <ChartThemeProvider>
        <VerticalStackedBarChart
          data={chartData}
          chartTitle={t('widgets.businessPurposeTitle')}
          yAxisTitle="%"
          barWidth={40}
          height={300}
          enableReflow={true}
        />
        </ChartThemeProvider>
      </div>
    </WidgetContainer>
  );
};

BusinessPurposeChart.displayName = 'BusinessPurposeChart';
